import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  ChefHat, Loader2, CheckCircle2, AlertCircle, UserPlus,
  KeyRound, Lock, Eye, EyeOff, RefreshCw, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const ROLE_COLORS = {
  manager:    "bg-indigo-100 text-indigo-700 border-indigo-200",
  kitchen:    "bg-orange-100 text-orange-700 border-orange-200",
  waiter:     "bg-emerald-100 text-emerald-700 border-emerald-200",
  accountant: "bg-blue-100 text-blue-700 border-blue-200",
};

// ─── OTP digit input ──────────────────────────────────────────────────
function OtpInput({ value, onChange }) {
  const DIGITS = 6;
  const refs = Array.from({ length: DIGITS }, () => useRef(null)); // eslint-disable-line

  const digits = value.split("").concat(Array(DIGITS).fill("")).slice(0, DIGITS);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      const next = value.slice(0, i) + value.slice(i + 1);
      onChange(next);
      if (i > 0) refs[i - 1].current?.focus();
      return;
    }
    if (!/^\d$/.test(e.key)) return;
    const next = (value.slice(0, i) + e.key + value.slice(i + 1)).slice(0, DIGITS);
    onChange(next);
    if (i < DIGITS - 1) refs[i + 1].current?.focus();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, DIGITS);
    if (paste) { onChange(paste); refs[Math.min(paste.length, DIGITS - 1)].current?.focus(); }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input-group">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={refs[i]}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={() => {}}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          data-testid={`otp-digit-${i}`}
          className={`w-11 h-12 text-center text-xl font-bold rounded-lg border-2 bg-background
            focus:outline-none focus:ring-0 transition-colors
            ${d ? "border-primary text-foreground" : "border-muted-foreground/30 text-muted-foreground"}`}
        />
      ))}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
export default function InvitationPage() {
  const { token } = useParams();
  const navigate  = useNavigate();

  const [invitation, setInvitation]   = useState(null);
  const [loadingInv, setLoadingInv]   = useState(true);
  const [invError, setInvError]       = useState(null);

  // Form state
  const [otp, setOtp]               = useState("");
  const [name, setName]             = useState("");
  const [password, setPassword]     = useState("");
  const [showPass, setShowPass]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted]     = useState(false);
  const [restaurantName, setRestaurantName] = useState("");

  // Attempt tracking
  const [attemptsLeft, setAttemptsLeft] = useState(5);
  const [blocked, setBlocked]           = useState(false);

  // Resend cooldown (seconds remaining)
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending]           = useState(false);
  const cooldownRef = useRef(null);

  // Already-logged-in path
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [acceptingDirect, setAcceptingDirect] = useState(false);

  // Load invitation
  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/invitations/${token}`
        ).then(r => {
          if (!r.ok) throw new Error(r.status === 410 ? "expirada" : "no_encontrada");
          return r.json();
        });
        setInvitation(data);
      } catch (e) {
        setInvError(e.message);
      } finally {
        setLoadingInv(false);
      }
    };
    load();
  }, [token]);

  // Check if already authenticated (cookie-based) — raw fetch to avoid redirect
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
          { credentials: "include" }
        );
        if (res.ok) setIsLoggedIn(true);
      } catch { /* not authenticated */ }
    };
    if (!loadingInv) checkAuth();
  }, [loadingInv]);

  // Cooldown timer
  const startCooldown = useCallback((seconds) => {
    setResendCooldown(seconds);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(s => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => clearInterval(cooldownRef.current), []);

  // Accept directly if already logged in
  const handleDirectAccept = async () => {
    setAcceptingDirect(true);
    try {
      const res = await api.request(`/invitations/${token}/accept`, { method: "POST" });
      setAccepted(true);
      setRestaurantName(res.restaurant_name || invitation?.restaurant_name || "");
      toast.success(res.message);
    } catch (e) {
      toast.error(e.message || "Error al aceptar la invitación");
    } finally {
      setAcceptingDirect(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    setResending(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/invitations/${token}/resend-otp`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      ).then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.detail || "Error al reenviar");
        }
        return r.json();
      });
      setOtp("");
      setBlocked(false);
      setAttemptsLeft(5);
      startCooldown(res.cooldown_seconds || 60);
      toast.success(res.message);
    } catch (e) {
      toast.error(e.message || "Error al reenviar el código");
    } finally {
      setResending(false);
    }
  };

  // OTP + password flow (main path for new/passwordless users)
  const handleVerifyAndJoin = async (e) => {
    e.preventDefault();
    if (blocked) return;
    if (otp.length < 6) { toast.error("Introduce el código completo de 6 dígitos"); return; }
    if (password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }

    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/invitations/${token}/verify-and-join`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ otp: otp.trim(), password, name: name.trim() || undefined }),
        }
      ).then(async r => {
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          const status = r.status;
          const errObj = new Error(err.detail || "Error al verificar el código");
          errObj.status = status;
          throw errObj;
        }
        return r.json();
      });

      setAccepted(true);
      setRestaurantName(res.restaurant_name || invitation?.restaurant_name || "");
      toast.success(res.message);
    } catch (e) {
      const msg = e.message || "Error al procesar la solicitud";
      // Parse remaining attempts from message e.g. "Te quedan 3 intentos"
      const match = msg.match(/(\d+) intento/);
      if (match) setAttemptsLeft(parseInt(match[1], 10));
      if (e.status === 429 || msg.includes("Demasiados")) {
        setBlocked(true);
        setAttemptsLeft(0);
      }
      setOtp(""); // clear OTP on error
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────
  if (loadingInv) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────
  if (invError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-10 pb-8">
            <AlertCircle className="w-14 h-14 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {invError === "expirada" ? "Invitación caducada" : "Invitación no encontrada"}
            </h2>
            <p className="text-muted-foreground text-sm mb-6">
              {invError === "expirada"
                ? "Este enlace ha expirado (72 h). Pide a tu administrador que te reenvíe la invitación."
                : "Este enlace no es válido o ya ha sido utilizado."}
            </p>
            <Button asChild variant="outline"><Link to="/login">Ir al inicio de sesión</Link></Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Accepted ───────────────────────────────────────────────────────
  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-10 pb-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-700" />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              ¡Bienvenido a {restaurantName || invitation?.restaurant_name}!
            </h2>
            <p className="text-muted-foreground text-sm mb-6">
              Ahora eres parte del equipo como{" "}
              <strong>{invitation?.role_label}</strong>.
              {!isLoggedIn && " Ya puedes iniciar sesión con tu email y contraseña."}
            </p>
            <Button onClick={() => navigate("/app")} data-testid="btn-go-to-app">
              Ir al panel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Main ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/30 p-4">
      <div className="w-full max-w-md space-y-4">

        {/* Branding */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 mb-2">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <ChefHat className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl">MenuGenius</span>
          </div>
        </div>

        {/* Invitation info */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  <strong>{invitation?.invited_by_name}</strong> te invita a
                </p>
                <p className="text-lg font-bold mt-0.5">{invitation?.restaurant_name}</p>
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
                    ${ROLE_COLORS[invitation?.role] || "bg-muted text-foreground"}`}>
                    {invitation?.role_label}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Already logged in path */}
        {isLoggedIn ? (
          <Card>
            <CardContent className="pt-6 pb-6 space-y-3">
              <p className="text-sm text-muted-foreground">
                Ya tienes sesión iniciada. Haz clic para unirte al restaurante.
              </p>
              <Button
                className="w-full"
                onClick={handleDirectAccept}
                disabled={acceptingDirect}
                data-testid="btn-direct-accept"
              >
                {acceptingDirect
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uniéndose...</>
                  : <><CheckCircle2 className="w-4 h-4 mr-2" />Unirme a {invitation?.restaurant_name}</>
                }
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* OTP + password flow */
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-primary" />
                Accede con tu código de invitación
              </CardTitle>
              <CardDescription className="text-xs leading-relaxed">
                Introduce el código de 6 dígitos que recibiste en el email
                <strong> {invitation?.email}</strong> y elige una contraseña para tu cuenta.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerifyAndJoin} className="space-y-5">

                {/* Blocked state */}
                {blocked && (
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-200 dark:bg-red-950/20 dark:border-red-800"
                       data-testid="otp-blocked-alert">
                    <ShieldAlert className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-700 dark:text-red-400">Código bloqueado</p>
                      <p className="text-xs text-red-700 dark:text-red-500 mt-0.5">
                        Has superado el límite de 5 intentos. Solicita un nuevo código.
                      </p>
                    </div>
                  </div>
                )}

                {/* OTP */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Código de un solo uso *</Label>
                    {attemptsLeft < 5 && !blocked && (
                      <span className={`text-xs font-medium ${attemptsLeft <= 2 ? "text-red-500" : "text-amber-500"}`}
                            data-testid="attempts-left">
                        {attemptsLeft} intento{attemptsLeft !== 1 ? "s" : ""} restante{attemptsLeft !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <OtpInput value={otp} onChange={setOtp} />
                  <p className="text-xs text-center text-muted-foreground">
                    Revisa tu bandeja de entrada (y spam) en {invitation?.email}
                  </p>
                </div>

                {/* Name */}
                <div>
                  <Label htmlFor="inv-name" className="text-sm">Tu nombre</Label>
                  <Input
                    id="inv-name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Nombre completo (opcional si ya tienes cuenta)"
                    className="mt-1.5"
                    data-testid="inv-name-input"
                  />
                </div>

                {/* Password */}
                <div>
                  <Label htmlFor="inv-pass" className="text-sm">Nueva contraseña *</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="inv-pass"
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      className="pr-10"
                      data-testid="inv-password-input"
                      disabled={blocked}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password && (
                    <div className="mt-1.5 flex gap-1">
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-colors ${
                            password.length > i * 3
                              ? password.length < 6 ? "bg-red-400"
                                : password.length < 10 ? "bg-amber-400"
                                : "bg-emerald-500"
                              : "bg-muted"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || blocked || otp.length < 6 || password.length < 6}
                  data-testid="btn-verify-and-join"
                >
                  {submitting
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verificando...</>
                    : <><Lock className="w-4 h-4 mr-2" />Verificar y unirme</>
                  }
                </Button>

                {/* Resend button */}
                <div className="text-center">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                    disabled={resending || resendCooldown > 0}
                    onClick={handleResend}
                    data-testid="btn-resend-otp"
                  >
                    {resending
                      ? <><Loader2 className="w-3 h-3 animate-spin" />Enviando...</>
                      : resendCooldown > 0
                        ? <><RefreshCw className="w-3 h-3" />Reenviar código ({resendCooldown}s)</>
                        : <><RefreshCw className="w-3 h-3" />Reenviar código</>
                    }
                  </Button>
                </div>

                <p className="text-xs text-center text-muted-foreground">
                  ¿Ya tienes cuenta con contraseña?{" "}
                  <Link to="/login" className="text-primary underline">
                    Inicia sesión
                  </Link>{" "}y luego vuelve a este enlace.
                </p>

              </form>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
}
